<template>
  <div 
    class="token-row"
    :class="{ 'clickable': clickable }"
    @click="handleClick"
  >
    <div class="token-icon-name">
      <div class="token-icon">{{ token.symbol.charAt(0) }}</div>
      <div class="token-info">
        <div class="token-name">{{ token.name }}</div>
        <div class="token-symbol">{{ token.symbol }}</div>
      </div>
    </div>
    
    <div class="token-price">
      <div class="current-price">{{ formatPrice(token.price) }}</div>
      <div 
        class="price-change" 
        :class="{ 
          'positive': token.priceChange > 0, 
          'negative': token.priceChange < 0 
        }"
      >
        {{ formatPercentage(token.priceChange) }}
      </div>
    </div>
    
    <div class="token-chart" v-if="showChart">
      <!-- Placeholder for mini chart -->
      <div 
        class="mini-chart"
        :class="{ 
          'positive': token.priceChange > 0, 
          'negative': token.priceChange < 0 
        }"
      >
        <div class="chart-line"></div>
      </div>
    </div>
    
    <div class="token-volume" v-if="showVolume">
      <div class="volume-label">Vol 24h</div>
      <div class="volume-value">{{ formatVolume(token.volume) }}</div>
    </div>
    
    <div class="token-balance" v-if="showBalance">
      <div class="balance-value">{{ formatBalance(token.balance) }}</div>
      <div class="balance-usd">{{ formatUSD(token.balance * token.price) }}</div>
    </div>
    
    <div class="token-actions" v-if="showActions">
      <button 
        class="action-button" 
        @click.stop="$emit('deposit', token)"
      >
        Deposit
      </button>
      <button 
        class="action-button" 
        @click.stop="$emit('withdraw', token)"
      >
        Withdraw
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const emit = defineEmits(['click', 'deposit', 'withdraw']);

const props = defineProps({
  token: {
    type: Object,
    required: true
  },
  showChart: {
    type: Boolean,
    default: false
  },
  showVolume: {
    type: Boolean,
    default: false
  },
  showBalance: {
    type: Boolean,
    default: false
  },
  showActions: {
    type: Boolean,
    default: false
  },
  clickable: {
    type: Boolean,
    default: false
  }
});

// Formatting functions
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: price >= 1000 ? 2 : (price >= 1 ? 4 : 8)
  }).format(price);
};

const formatPercentage = (percentage) => {
  const sign = percentage > 0 ? '+' : '';
  return `${sign}${percentage.toFixed(2)}%`;
};

const formatVolume = (volume) => {
  if (volume >= 1000000000) {
    return `$${(volume / 1000000000).toFixed(1)}B`;
  }
  if (volume >= 1000000) {
    return `$${(volume / 1000000).toFixed(1)}M`;
  }
  if (volume >= 1000) {
    return `$${(volume / 1000).toFixed(1)}K`;
  }
  return `$${volume.toFixed(0)}`;
};

const formatBalance = (balance) => {
  if (balance >= 1000) {
    return balance.toFixed(2);
  }
  if (balance >= 1) {
    return balance.toFixed(4);
  }
  return balance.toFixed(8);
};

const formatUSD = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Click handler
const handleClick = () => {
  if (!props.clickable) return;
  
  emit('click', props.token);
  
  // Navigate to trade page with token as parameter
  router.push(`/trade/${props.token.symbol}`);
};
</script>

<style scoped>
.token-row {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #252f3d;
  transition: background-color 0.2s;
}

.token-row:last-child {
  border-bottom: none;
}

.token-row.clickable {
  cursor: pointer;
}

.token-row.clickable:hover {
  background-color: #1a2334;
}

.token-icon-name {
  display: flex;
  align-items: center;
  flex: 2;
  min-width: 180px;
}

.token-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #1347e4;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  margin-right: 12px;
  flex-shrink: 0;
}

.token-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.token-name {
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.token-symbol {
  font-size: 14px;
  color: #788195;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.token-price {
  flex: 1;
  min-width: 100px;
  text-align: right;
}

.current-price {
  font-weight: 500;
  color: #fff;
}

.price-change {
  font-size: 14px;
}

.price-change.positive {
  color: #00c582;
}

.price-change.negative {
  color: #e74c3c;
}

.token-chart {
  flex: 1;
  min-width: 80px;
  height: 40px;
  padding: 0 10px;
}

.mini-chart {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.chart-line {
  width: 100%;
  height: 2px;
  position: relative;
}

.mini-chart.positive .chart-line {
  background: linear-gradient(90deg, transparent, #00c582);
}

.mini-chart.negative .chart-line {
  background: linear-gradient(90deg, transparent, #e74c3c);
}

.mini-chart.positive .chart-line::after,
.mini-chart.negative .chart-line::after {
  content: '';
  position: absolute;
  right: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.mini-chart.positive .chart-line::after {
  background-color: #00c582;
  top: -2px;
}

.mini-chart.negative .chart-line::after {
  background-color: #e74c3c;
  top: -2px;
}

.token-volume {
  flex: 1;
  min-width: 100px;
  text-align: right;
}

.volume-label {
  font-size: 12px;
  color: #788195;
}

.volume-value {
  color: #fff;
}

.token-balance {
  flex: 1;
  min-width: 100px;
  text-align: right;
}

.balance-value {
  font-weight: 500;
  color: #fff;
}

.balance-usd {
  font-size: 14px;
  color: #788195;
}

.token-actions {
  display: flex;
  gap: 8px;
  margin-left: 16px;
}

.action-button {
  padding: 6px 12px;
  background-color: #1a2334;
  border: 1px solid #252f3d;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.action-button:hover {
  background-color: #252f3d;
}

@media (max-width: 1024px) {
  .token-chart {
    display: none;
  }
}

@media (max-width: 768px) {
  .token-row {
    flex-wrap: wrap;
    padding: 12px;
  }
  
  .token-icon-name {
    flex: 1 0 60%;
    margin-bottom: 8px;
    min-width: auto;
  }
  
  .token-price {
    flex: 1 0 40%;
    text-align: right;
    min-width: auto;
  }
  
  .token-volume,
  .token-balance {
    flex: 1 0 50%;
    margin-top: 8px;
    min-width: auto;
  }
  
  .token-actions {
    flex: 1 0 100%;
    justify-content: space-between;
    margin-left: 0;
    margin-top: 12px;
    gap: 4px;
  }
  
  .action-button {
    flex: 1;
    padding: 8px 0;
    font-size: 13px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .token-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
    margin-right: 8px;
  }
  
  .token-name {
    font-size: 14px;
  }
  
  .token-symbol {
    font-size: 12px;
  }
  
  .current-price {
    font-size: 14px;
  }
  
  .price-change,
  .volume-label,
  .volume-value,
  .balance-value,
  .balance-usd {
    font-size: 12px;
  }
}
</style> 